/**
 * save-group-control.js
 */

import React from 'react';
import { alpha, TextField, Button, ButtonGroup, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useFormContext, useWatch } from 'react-hook-form';
import analytics from 'utils/analytics';
import { UserContext } from 'contexts/user/user-context';
import Dialog from 'components/dialog';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import { TrainingGroupsContext } from 'routing/routes/config/groups/training/training-groups-context';


const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialogContent-root': {
      paddingTop: theme.spacing(3),
    },
  },
  actions: {
    background: alpha(theme.palette.sys.primary, 0.05),
    color: theme.palette.sys.primary,
    border: `1px solid ${alpha(theme.palette.sys.primary, 0.5)}`,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing(.5)} 0 ${theme.spacing(.5)} ${theme.spacing(2)}`,
  },
  actionHeading: {
    fontSize: theme.typography.sizes[1],
    fontWeight: 500,
    marginRight: theme.spacing(2),
  },
  button: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
  },
  nameInput: {
    marginTop: theme.spacing(2),
  },
}));


export default function SaveGroupControl(props) {
  const {
    isGroupUnsynced,
  } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = React.useContext(UserContext);
  const { groups, onSaveGroup } = React.useContext(TrainingGroupsContext);
  const formContext = useFormContext();
  const { control, setValue } = formContext;
  const groupId = useWatch({ control, name: 'groupId' });
  const members = useWatch({ control, name: 'members' });
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState();
  const inputRef = React.useRef();
  const group = groups.find(g => g.id === groupId);
  const isLeader = Boolean(group?.members?.find(m => m.id === user.id && m.isLeader));
  const isMembersEmpty = members.length === 0;
  const isUpdateDisabled = !isGroupUnsynced || !isLeader || isMembersEmpty;
  const isAddDisabled = (groupId !== null && !isGroupUnsynced) || isMembersEmpty;
  const isValid = isUpdate || inputText;

  const updateBtnTooltip = (() => {
    if (isMembersEmpty) {
      return 'The member list is empty';
    } else if (isGroupUnsynced && !isLeader) {
      return 'You must be a group leader to resave the selected group using the members listed above';
    } else if (groupId === null) {
      return 'No group is selected';
    } else if (!isGroupUnsynced) {
      return 'No changes have been made to the member list';
    } else {
      return 'Resave the selected group using the members listed above';
    }
  })();

  const addBtnTooltip = (() => {
    if (isMembersEmpty) {
      return 'The member list is empty';
    } else if (isAddDisabled) {
      return 'No changes have been made to the member list';
    } else {
      return 'Create a new group using the members listed above';
    }
  })();


  React.useEffect(() => {
     if (isOpen && !isUpdate) {
       setInputText('');
       setTimeout(() => inputRef.current?.focus());
     }
   }, [isOpen, isUpdate]);


  const handleBtnClick = (update) => {
    setIsUpdate(update);
    setIsOpen(true);
  };

  const onOk = async () => {
    try {
      const response = await onSaveGroup({
        id: isUpdate ? group.id : undefined,
        name: isUpdate ? group.name : inputText,
        members: members,
      }, setIsLoading);
      if (response.status === 200) {
        analytics.trackGA4('event', { name: 'group_save', params: { save_type: isUpdate ? 'edited' : 'added', page_type: 'event' } }); 
        setValue('groupId', response.data.id, { shouldDirty: true });
        enqueueSnackbar(`Group ${isUpdate ? 'updated' : 'created'}`, {
          variant: 'success'
        });      
      }
    } finally {
      setIsOpen(false);
    }
  }


  const onKeyDown = (e) => {
    if (isValid && e.key === 'Enter') {
      onOk();
    }
  }
 

  return (
    <>
      <div className={classes.actions}>
        <Typography
          component='span'
          className={classes.actionHeading}
        >
          GROUP
        </Typography>
        <ButtonGroup variant="text">
          <Tooltip 
            arrow 
            title={updateBtnTooltip}
          >
            <span>
              <Button
                color='primary'
                variant='text'
                size='small'
                disabled={isUpdateDisabled}
                className={classes.button}
                onClick={() => handleBtnClick(true)}
                children='Update'
                startIcon={<Icon type='sync' />}
              />
            </span>
          </Tooltip>
          <Tooltip
            arrow
            title={addBtnTooltip}
          >
            <span>
              <Button
                color='primary'
                variant='text'
                size='small'
                disabled={isAddDisabled}
                className={classes.button}
                onClick={() => handleBtnClick(false)}
                children='New'
                startIcon={<Icon type='add' />}
              />
            </span>
          </Tooltip>
        </ButtonGroup>
      </div>

      <Dialog
        fullWidth
        maxWidth='xs'
        title={isUpdate ? 'Update Group' : 'Create New Group'}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        okLabel={isUpdate ? 'Update' : 'Save'}
        okDisabled={isLoading || !isValid}
        onOk={onOk}
        contentWrap={<FeatureBox loading={isLoading} />}
        className={classes.dialog}
        children={<>
          {isUpdate
            ? <Typography>
                Press the UPDATE button to overwrite the existing training group <b>{group?.name}</b>{' '} 
                using the members and leader settings defined in the current event.
              </Typography>
            : <>
              <Typography>
                Type a name for your new group. The group will include the members and leader settings defined in this event.
              </Typography>
              <TextField
                fullWidth
                className={classes.nameInput}
                inputRef={inputRef}
                label={'Group name'}
                variant='outlined'
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </>}
        </>}
      />
    </>
  );
}